/* eslint-disable no-redeclare */

import produce from 'immer';
import { isArray, isObject } from './typeUtils';

/**
 * Safely compare two immutables
 * @param {T} obj1 First object to compare
 * @param {T} obj2 Second object to compare
 * @param {K1} key1 Property name (1st level)
 * @returns boolean
 */
export function changed<T, K1 extends keyof Required<T>>(obj1: T, obj2: T, key1: K1): boolean;
/**
 * Safely compare two immutables
 * @param {T} obj1 First object to compare
 * @param {T} obj2 Second object to compare
 * @param {K1} key1 Property name (1st level)
 * @param {K2} key2 Property name (2nd level)
 * @returns boolean
 */
export function changed<T, K1 extends keyof Required<T>, K2 extends keyof Required<Required<T>[K1]>>(
  obj1: T,
  obj2: T,
  key1: K1,
  key2: K2
): boolean;
/**
 * Safely compare two immutables
 * @param {T} obj1 First object to compare
 * @param {T} obj2 Second object to compare
 * @param {K1} key1 Property name (1st level)
 * @param {K2} key2 Property name (2nd level)
 * @param {K3} key3 Property name (3rd level)
 * @returns boolean
 */
export function changed<
  T,
  K1 extends keyof Required<T>,
  K2 extends keyof Required<Required<T>[K1]>,
  K3 extends keyof Required<Required<Required<T>[K1]>[K2]>
>(obj1: T, obj2: T, key1: K1, key2: K2, key3: K3): boolean;

/**
 * Safely compare two immutables
 * @param {T} obj1 First object to compare
 * @param {T} obj2 Second object to compare
 * @param {K1} key1 Property name (1st level)
 * @param {K2} key2 Property name (2nd level)
 * @param {K3} key3 Property name (3rd level)
 * @param {K4} key4 Property name (4th level)
 * @returns boolean
 */
export function changed<
  T,
  K1 extends keyof Required<T>,
  K2 extends keyof Required<Required<T>[K1]>,
  K3 extends keyof Required<Required<Required<T>[K1]>[K2]>,
  K4 extends keyof Required<Required<Required<Required<T>[K1]>[K2]>[K3]>
>(obj1: T, obj2: T, key1: K1, key2: K2, key3: K3, key4: K4): boolean;
export function changed(obj1: unknown, obj2: unknown, ...path: string[]): any {
  const compare1 = path.reduce((object, key) => object && object?.[key], obj1);
  const compare2 = path.reduce((object, key) => object && object?.[key], obj2);

  if (isObject(compare1)) {
    return JSON.stringify(compare1) !== JSON.stringify(compare2);
  }

  return compare1 !== compare2;
}

export type Comparator<T> = (t1: T, t2: T) => boolean;

/**
 * Create comparison function for two immutables based on given path
 * @param {K1} key1 Property name (1st level)
 * @returns comparator function
 */
export function changedFactory<T, K1 extends keyof Required<T>>(key1: K1): Comparator<T>;
/**
 * Create comparison function for two immutables based on given path
 * @param {K1} key1 Property name (1st level)
 * @param {K2} key2 Property name (2nd level)
 * @returns comparator function
 */
export function changedFactory<T, K1 extends keyof Required<T>, K2 extends keyof Required<Required<T>[K1]>>(
  key1: K1,
  key2: K2
): Comparator<T>;
/**
 * Create comparison function for two immutables based on given path
 * @param {K1} key1 Property name (1st level)
 * @param {K2} key2 Property name (2nd level)
 * @param {K3} key3 Property name (3rd level)
 * @returns comparator function
 */
export function changedFactory<
  T,
  K1 extends keyof Required<T>,
  K2 extends keyof Required<Required<T>[K1]>,
  K3 extends keyof Required<Required<Required<T>[K1]>[K2]>
>(key1: K1, key2: K2, key3: K3): Comparator<T>;
export function changedFactory(...path: string[]): Comparator<unknown> {
  return (obj1: unknown, obj2: unknown): boolean => (changed as any)(obj1, obj2, ...path);
}

const mergeInto = (a: any, b: any): Record<string, unknown> => {
  if (!isObject(b)) {
    return b;
  }
  if (isArray(b)) {
    if (!isArray(a)) a = [];

    if (a.length > b.length) {
      a = a.slice(0, b.length);
    }
    b.forEach((val, idx) => {
      if (idx < a.length) {
        a[idx] = mergeInto(a[idx] || {}, b[idx]);
      } else {
        a.push(b[idx]);
      }
    });
    return a;
  }
  Object.keys(b).forEach((key: string) => (a[key] = mergeInto(a[key] || {}, b[key])));
  return a;
};

/** Produce new state by merging changes into draft */
export const produceMerge = produce(mergeInto);
